import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@core/store/hooks';

import {
  useCreateOrganizationMutation,
  useImpersonateOrganizationMutation,
  useStopImpersonationMutation,
  useSwitchOrganizationMutation,
} from '../api/authApi';
import { selectCsrfToken, setCredentials } from '../store/authSlice';

/**
 * Owns "adopt the session returned by a tenant change".
 *
 * Switching organization, creating one (staff) and starting or stopping
 * impersonation (superuser) all return a fresh `{user, csrf_token}` payload.
 * Dispatching setCredentials — and not restoreSession — matters: it is the
 * action wired to the store listener that purges every tenant-bound cache
 * and project selection.
 */
export function useSwitchOrganization() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentCsrfToken = useAppSelector(selectCsrfToken);
  const [switchOrganization, { isLoading: isSwitching }] = useSwitchOrganizationMutation();
  const [createOrganization, { isLoading: isCreating }] = useCreateOrganizationMutation();
  const [impersonateOrganization, { isLoading: isStartingImpersonation }] = useImpersonateOrganizationMutation();
  const [stopImpersonationMutation, { isLoading: isStoppingImpersonation }] = useStopImpersonationMutation();

  const adoptSession = useCallback(
    (result) => {
      dispatch(setCredentials({ user: result.user, csrfToken: result.csrf_token ?? currentCsrfToken }));
      navigate('/', { replace: true });
    },
    [dispatch, navigate, currentCsrfToken],
  );

  const switchTo = useCallback(
    async (organizationUuid) => {
      const result = await switchOrganization(organizationUuid).unwrap();
      adoptSession(result);
    },
    [switchOrganization, adoptSession],
  );

  const createAndSwitch = useCallback(
    async (name) => {
      const result = await createOrganization(name).unwrap();
      adoptSession(result);
    },
    [createOrganization, adoptSession],
  );

  const impersonate = useCallback(
    async (organizationUuid) => {
      const result = await impersonateOrganization(organizationUuid).unwrap();
      adoptSession(result);
    },
    [impersonateOrganization, adoptSession],
  );

  const stopImpersonating = useCallback(
    async () => {
      const result = await stopImpersonationMutation().unwrap();
      adoptSession(result);
    },
    [stopImpersonationMutation, adoptSession],
  );

  return {
    switchTo,
    createAndSwitch,
    impersonate,
    stopImpersonating,
    isSwitching,
    isCreating,
    isStartingImpersonation,
    isStoppingImpersonation,
  };
}
